import { useTrackers } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CasinoGameIcon, CasinoGameTile, CasinoPlaySelector, Info, PlayBar } from '@swf/casino';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { isCompleted, isRunning } from '@swf/core/utils/casino';

import styles from './Race.scss';

interface Props {
  count: number;
  onLeaderboard?(): void;
  onLaunch?(): void;
  race: CasinoRace;
  widgetId?: string;
  path?: string;
}

const Race: React.FC<Props> = ({ count, onLaunch, onLeaderboard, race, widgetId, path }) => {
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const { casino, modal } = useDispatch<Dispatch>();
  const { gameInfo, multiGameList, id, name, raceTypes, status, tncVersion } = race;
  const hasMultiGames = multiGameList && multiGameList.length > 1;
  const running = isRunning(status);
  const canJoin = !(isCompleted(status) || (isPlayMoney && !running));
  const { buttonPressed } = useTrackers();

  const handleLaunch = useCallback(() => {
    buttonPressed({ buttonName: 'Play' });
    onLaunch?.();
  }, [buttonPressed, onLaunch]);

  const openGameSelector = useCallback(() => {
    handleLaunch();
    modal.enqueue({ path: '/casino/game-selector', locationState: { widgetId, path } });
  }, [handleLaunch, modal, path, widgetId]);

  const launchRace = useCallback(() => {
    if (running && hasMultiGames) {
      openGameSelector();
    } else {
      handleLaunch();
      casino.race({ game: gameInfo, raceId: id, raceTypes, tncVersion, join: !running, widgetId });
    }
  }, [
    casino,
    gameInfo,
    handleLaunch,
    hasMultiGames,
    id,
    openGameSelector,
    raceTypes,
    tncVersion,
    running,
    widgetId
  ]);

  return (
    <>
      <div className={classNames(styles.container, !onLeaderboard && styles.leaderboardContainer)}>
        <div className={classNames(styles.gameIconHolder, !onLeaderboard && styles.leaderboard)}>
          {hasMultiGames || !canJoin ? (
            <CasinoGameIcon
              className={styles.gameIcon}
              game={gameInfo}
              race={race}
              icon={hasMultiGames ? 'icnRace' : 'icnSquare'}
              tooltip={name}
              disabled={!canJoin}
            >
              {canJoin && (
                <CasinoPlaySelector hover hideFeatureButtons onClick={openGameSelector} />
              )}
            </CasinoGameIcon>
          ) : (
            <CasinoGameTile
              className={styles.gameIcon}
              icon="icnSquare"
              game={race.gameInfo}
              race={race}
              onLaunch={onLaunch}
            />
          )}
        </div>
        <Info race={race} count={count} onLeaderboard={onLeaderboard} />
      </div>
      <PlayBar race={race} onLeaderboard={onLeaderboard} onLaunch={launchRace} />
    </>
  );
};

export default Race;
